<template>
  <div ref="mediaWrapper" class="secured-media-wrapper" :class="'type-' + mediaType" @click="$emit('click')">
    <div v-if="isLoading && hasBeenVisible" class="d-flex justify-center align-center secured-media-loader">
      <v-progress-circular indeterminate color="white" size="24" width="2"></v-progress-circular>
    </div>

    <div v-if="!hasBeenVisible" class="d-flex justify-center align-center secured-media-loader">
      <v-icon color="rgba(255,255,255,0.3)">mdi-eye-off-outline</v-icon>
    </div>

    <img v-else-if="mediaType === 'image' && blobUrl" :src="blobUrl" class="secured-media-content" />

    <div v-else-if="mediaType === 'video' && blobUrl" class="video-wrapper">
      <video :src="blobUrl" controls class="secured-media-content"></video>
    </div>

    <div v-else-if="mediaType === 'audio' && blobUrl" class="audio-player-pill">
      <audio ref="audioPlayer" :src="blobUrl" @loadedmetadata="onAudioLoaded" @timeupdate="updateProgress" @ended="onAudioEnded"></audio>

      <div class="play-btn-circle" @click.stop="togglePlay">
        <v-icon color="white" size="20">
          {{ isPlaying ? "mdi-pause" : "mdi-play" }}
        </v-icon>
      </div>

      <div class="waveform-container" @click.stop="seekAudio">
        <canvas ref="waveformCanvas"></canvas>
      </div>

      <span class="audio-time-label">
        {{ isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration) }}
      </span>
    </div>

    <div v-else-if="hasLoaded && !blobUrl" class="secured-media-error">
      <v-icon small color="red lighten-2" class="mr-2">mdi-alert-circle</v-icon>
      <span class="caption red--text text--lighten-2">Fehler</span>
    </div>
  </div>
</template>

<script>
import api from "@/api"

const mediaCache = new Map()
let audioContext = null
let currentAudioInstance = null

export default {
  name: "SecuredMedia",
  props: {
    filename: { type: String, required: true },
    scrollContainer: { default: null },
    noObserver: { type: Boolean, default: false },
    mediaType: { type: String, required: true },
  },
  data() {
    return {
      isLoading: false,
      blobUrl: null,
      hasBeenVisible: false,
      hasLoaded: false,
      observer: null,

      // Audio Data
      isPlaying: false,
      duration: 0,
      currentTime: 0,
      waveform: [],
      animationFrameId: null,
    }
  },

  watch: {
    scrollContainer(newContainer) {
      if (newContainer && !this.hasBeenVisible && !this.noObserver) {
        this.recreateObserver()
      }
    },
  },

  mounted() {
    if (this.noObserver) {
      this.hasBeenVisible = true
      this.loadMedia()
      return
    }
    this.createObserver()
  },

  beforeDestroy() {
    this.stopPlayback()
    this.disconnectObserver()
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
  },

  methods: {
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    recreateObserver() {
      this.disconnectObserver()
      this.$nextTick(() => {
        this.createObserver()
      })
    },
    createObserver() {
      if (this.hasBeenVisible) return
      if (!this.$refs.mediaWrapper) {
        setTimeout(this.createObserver, 100)
        return
      }
      const options = { root: this.scrollContainer, threshold: 0.01, rootMargin: "50px" }
      this.observer = new IntersectionObserver(this.handleIntersect, options)
      this.observer.observe(this.$refs.mediaWrapper)
    },
    handleIntersect(entries) {
      if (entries[0].isIntersecting && !this.hasBeenVisible) {
        this.hasBeenVisible = true
        this.loadMedia()
        this.disconnectObserver()
      }
    },

    async loadMedia() {
      if (!this.filename || this.hasLoaded) return

      const cachedBlob = mediaCache.get(this.filename)
      if (cachedBlob) {
        this.blobUrl = URL.createObjectURL(cachedBlob)
        if (this.mediaType === "audio") {
          setTimeout(async () => {
            const arrayBuffer = await cachedBlob.arrayBuffer()
            this.processAudio(arrayBuffer)
          }, 50)
        }
        this.hasLoaded = true
        return
      }

      this.isLoading = true
      this.hasLoaded = false
      try {
        const response = await api.fetchSecuredMedia(this.filename)
        let blob = response.data
        if (!(blob instanceof Blob)) {
          blob = new Blob([response.data], { type: response.headers["content-type"] })
        }

        mediaCache.set(this.filename, blob)
        this.blobUrl = URL.createObjectURL(blob)

        if (this.mediaType === "audio") {
          const arrayBuffer = await blob.arrayBuffer()
          setTimeout(() => this.processAudio(arrayBuffer), 50)
        }
      } catch (err) {
        console.error("Media load error:", err)
      } finally {
        this.isLoading = false
        this.hasLoaded = true
      }
    },

    onAudioLoaded() {
      if (this.$refs.audioPlayer) {
        this.duration = this.$refs.audioPlayer.duration
        this.attemptDraw()
      }
    },
    updateProgress() {
      if (this.$refs.audioPlayer) {
        this.currentTime = this.$refs.audioPlayer.currentTime
        if (!this.isPlaying) this.drawWaveform()
      }
    },
    stopPlayback() {
      if (this.isPlaying) {
        this.isPlaying = false
        if (this.$refs.audioPlayer) this.$refs.audioPlayer.pause()
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId)
          this.animationFrameId = null
        }
        if (currentAudioInstance === this) currentAudioInstance = null
      }
    },
    externalPause() {
      this.stopPlayback()
    },
    togglePlay() {
      const player = this.$refs.audioPlayer
      if (!player) return

      if (this.isPlaying) {
        this.stopPlayback()
      } else {
        if (currentAudioInstance && currentAudioInstance !== this) {
          currentAudioInstance.externalPause()
        }
        this.isPlaying = true
        currentAudioInstance = this
        player.play()
        this.animateLoop()
      }
    },
    animateLoop() {
      if (!this.isPlaying) return
      if (this.$refs.audioPlayer) this.currentTime = this.$refs.audioPlayer.currentTime
      this.drawWaveform()
      this.animationFrameId = requestAnimationFrame(this.animateLoop)
    },
    onAudioEnded() {
      this.stopPlayback()
      this.currentTime = 0
      if (this.$refs.audioPlayer) this.$refs.audioPlayer.currentTime = 0
      this.drawWaveform()
    },
    seekAudio(event) {
      const player = this.$refs.audioPlayer
      if (!player || !this.duration) return
      const bounds = event.currentTarget.getBoundingClientRect()
      if (bounds.width === 0) return
      const clickPercent = (event.clientX - bounds.left) / bounds.width
      const newTime = this.duration * clickPercent
      player.currentTime = newTime
      this.currentTime = newTime
      this.drawWaveform()
    },

    async processAudio(arrayBuffer) {
      try {
        if (!audioContext) audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        const channelData = audioBuffer.getChannelData(0)

        const samples = 30
        const blockSize = Math.floor(channelData.length / samples)
        const waveform = []

        for (let i = 0; i < samples; i++) {
          let sum = 0
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[i * blockSize + j])
          }
          waveform.push((sum / blockSize) * 1.5)
        }

        const maxAmp = Math.max(...waveform) || 1
        this.waveform = waveform.map((amp) => Math.max(0.2, Math.min(1, amp / maxAmp)))

        this.attemptDraw()
      } catch (e) {
        console.error("Audio Analysis Error:", e)
        this.waveform = Array(30)
          .fill(0.3)
          .map(() => 0.2 + Math.random() * 0.4)
        this.attemptDraw()
      }
    },

    attemptDraw(retries = 3) {
      if (!this.$refs.waveformCanvas) return
      if (this.$refs.waveformCanvas.offsetWidth === 0 && retries > 0) {
        requestAnimationFrame(() => this.attemptDraw(retries - 1))
        return
      }
      this.drawWaveform()
    },

    formatTime(seconds) {
      if (isNaN(seconds)) return "0:00"
      const min = Math.floor(seconds / 60)
      const sec = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0")
      return `${min}:${sec}`
    },

    // --- HIER WAR DER BUG-FIX ---
    drawWaveform() {
      const canvas = this.$refs.waveformCanvas
      if (!canvas || !this.waveform.length) return

      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0) return

      // Resize Handling
      if (canvas.width !== rect.width * dpr) {
        canvas.width = rect.width * dpr
        canvas.height = rect.height * dpr
      }

      const ctx = canvas.getContext("2d")

      // 1. RESET TRANSFORM (DER WICHTIGSTE FIX!)
      // Setzt den Zeichenkontext zurück, damit scale() nicht akkumuliert
      ctx.setTransform(1, 0, 0, 1, 0, 0)

      // 2. Alles löschen (Physische Pixel)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 3. Neu skalieren für HiDPI
      ctx.scale(dpr, dpr)

      // Ab hier normale Zeichenlogik in logischen Pixeln (CSS)
      const width = rect.width
      const height = rect.height
      const barGap = 3
      const totalBars = this.waveform.length
      const barWidth = (width - (totalBars - 1) * barGap) / totalBars
      const centerY = height / 2

      const progress = this.duration > 0 ? this.currentTime / this.duration : 0

      for (let i = 0; i < totalBars; i++) {
        const x = i * (barWidth + barGap)
        const val = this.waveform[i]
        const barHeight = height * 0.8 * val
        const barPos = i / totalBars
        const isPlayed = barPos < progress

        ctx.beginPath()
        ctx.lineCap = "round"
        ctx.lineWidth = barWidth

        ctx.moveTo(x + barWidth / 2, centerY - barHeight / 2)
        ctx.lineTo(x + barWidth / 2, centerY + barHeight / 2)

        if (isPlayed) {
          ctx.strokeStyle = "rgba(255, 255, 255, 1.0)"
        } else {
          ctx.strokeStyle = "rgba(255, 255, 255, 0.4)"
        }
        ctx.stroke()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.secured-media-wrapper {
  position: relative;
  display: inline-block;
}

/* SPEZIAL-STYLING FÜR AUDIO (DIE PILLE) */
.audio-player-pill {
  display: flex;
  align-items: center;
  /* ETWAS SCHMALER, DAMIT PLATZ FÜR TEXT IST */
  width: 220px;
  height: 48px;
  padding: 0 6px; /* Weniger Padding links/rechts */
}

.play-btn-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;

  transition: transform 0.1s, background 0.2s;

  &:active {
    transform: scale(0.92);
    background: rgba(255, 255, 255, 0.25);
  }
}

.waveform-container {
  flex-grow: 1;
  height: 100%;
  /* Etwas Luft für den Canvas */
  margin: 0 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  pointer-events: auto;
}

.waveform-container canvas {
  width: 100%;
  height: 32px;
  display: block;
}

.audio-time-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  /* Mehr Mindestbreite, damit nichts abgeschnitten wird */
  min-width: 40px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  margin-right: 2px; /* Kleiner Abstand zum Rand */
}

/* Loader & Error States */
.secured-media-loader,
.secured-media-error {
  width: 100%;
  min-width: 200px;
  height: 100px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
